import { motion } from "framer-motion";
import { Phone, MapPin, Clock, Mail } from "lucide-react";
import { RevealOnScroll } from "./RevealText";
import { BookingForm } from "./BookingForm";
import { WhatsAppIcon } from "./icons";
import { PHONE_DISPLAY, PHONE_TEL, EMAIL, whatsappUrl } from "../../lib/leads";

export const Contact = () => {
  return (
    <section id="contact" data-testid="contact-section" className="relative bg-white py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div>
            <RevealOnScroll>
              <span className="text-xs uppercase tracking-[0.3em] text-jodhpur-accent font-semibold">Get in touch</span>
              <h2 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl text-jodhpur-primary tracking-tight">
                Let's plan your <span className="italic">ride.</span>
              </h2>
              <p className="mt-5 text-jodhpur-ink/65 max-w-md leading-relaxed">
                Call us, drop a WhatsApp, or fill the form — our team responds within minutes, round the clock.
              </p>
            </RevealOnScroll>

            <div className="mt-8 space-y-4">
              <a href={`tel:${PHONE_TEL}`} data-testid="contact-call" className="group flex items-center gap-4 rounded-2xl border border-jodhpur-primary/10 bg-jodhpur-bg p-4 hover:border-jodhpur-secondary transition-colors">
                <span className="h-12 w-12 rounded-xl bg-jodhpur-primary flex items-center justify-center text-white"><Phone className="h-5 w-5" /></span>
                <div>
                  <p className="text-xs uppercase tracking-wider text-jodhpur-ink/50">Call us</p>
                  <p className="font-semibold text-jodhpur-primary text-lg">{PHONE_DISPLAY}</p>
                </div>
              </a>
              <a href={whatsappUrl("Namaste Jodhpur Cabs! I'd like to book a taxi.")} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp" className="group flex items-center gap-4 rounded-2xl border border-jodhpur-primary/10 bg-jodhpur-bg p-4 hover:border-[#25D366] transition-colors">
                <span className="h-12 w-12 rounded-xl bg-[#25D366] flex items-center justify-center text-white"><WhatsAppIcon className="h-5 w-5" /></span>
                <div>
                  <p className="text-xs uppercase tracking-wider text-jodhpur-ink/50">WhatsApp</p>
                  <p className="font-semibold text-jodhpur-primary text-lg">{PHONE_DISPLAY}</p>
                </div>
              </a>
              <div className="flex items-start gap-4 rounded-2xl border border-jodhpur-primary/10 bg-jodhpur-bg p-4">
                <span className="h-12 w-12 rounded-xl bg-jodhpur-accent flex items-center justify-center text-white shrink-0"><MapPin className="h-5 w-5" /></span>
                <div>
                  <p className="text-xs uppercase tracking-wider text-jodhpur-ink/50">Visit us</p>
                  <p className="font-medium text-jodhpur-ink leading-snug">Rukma Nagar, AIIMS Road, Kheme Ka Kuwa,<br />Jodhpur, Rajasthan 342008</p>
                </div>
              </div>
              <div className="flex items-center gap-4 rounded-2xl border border-jodhpur-primary/10 bg-jodhpur-bg p-4">
                <span className="h-12 w-12 rounded-xl bg-jodhpur-secondary flex items-center justify-center text-white"><Clock className="h-5 w-5" /></span>
                <div>
                  <p className="text-xs uppercase tracking-wider text-jodhpur-ink/50">Hours</p>
                  <p className="font-semibold text-jodhpur-primary">Open 24 × 7, all days</p>
                </div>
              </div>
              <a href={`mailto:${EMAIL}`} data-testid="contact-email" className="group flex items-center gap-4 rounded-2xl border border-jodhpur-primary/10 bg-jodhpur-bg p-4 hover:border-jodhpur-secondary transition-colors">
                <span className="h-12 w-12 rounded-xl bg-jodhpur-primary flex items-center justify-center text-white"><Mail className="h-5 w-5" /></span>
                <div>
                  <p className="text-xs uppercase tracking-wider text-jodhpur-ink/50">Email</p>
                  <p className="font-semibold text-jodhpur-primary text-lg break-all">{EMAIL}</p>
                </div>
              </a>
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <BookingForm formId="contact-booking" />
          </motion.div>
        </div>

        <div className="mt-14" data-testid="map-block">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-4">
            <h3 className="font-display text-2xl sm:text-3xl text-jodhpur-primary">Find us on the map</h3>
            <a
              href="https://www.google.com/maps/dir/?api=1&destination=Rukma+Nagar+AIIMS+Road+Kheme+Ka+Kuwa+Jodhpur+Rajasthan+342008"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="get-directions"
              className="inline-flex items-center gap-2 rounded-full bg-jodhpur-primary text-white px-5 py-2.5 text-sm font-semibold hover:bg-jodhpur-secondary transition-colors w-fit"
            >
              <MapPin className="h-4 w-4" /> Get Directions
            </a>
          </div>
          <div className="relative rounded-3xl overflow-hidden border border-jodhpur-primary/10 shadow-sm h-[300px] sm:h-[380px]" data-testid="google-map">
            {/* Verified static map fallback (always visible if the Google embed is blocked) */}
            <img
              src="https://static-maps.yandex.ru/1.x/?ll=72.987488,26.240170&z=15&size=650,380&l=map&pt=72.987488,26.240170,pm2rdm"
              alt="Jodhpur Cabs location — Rukma Nagar, AIIMS Road, Kheme Ka Kuwa, Jodhpur"
              loading="lazy"
              decoding="async"
              className="absolute inset-0 z-0 w-full h-full object-cover"
            />
            {/* Official Google Maps embed with the real business pin (renders on live browsers) */}
            <iframe
              title="Jodhpur Cabs location on Google Maps"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3578.661991031727!2d72.98748787451386!3d26.24017017705439!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x39418f0b9cba5c45%3A0x8d7395bcfad5d76b!2sJodhpur%20cabs!5e0!3m2!1sen!2sin!4v1784712495976!5m2!1sen!2sin"
              className="absolute inset-0 z-10 w-full h-full"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="strict-origin-when-cross-origin"
            />
          </div>
          <div className="mt-3 flex items-center gap-2 text-sm text-jodhpur-ink/60">
            <MapPin className="h-4 w-4 text-jodhpur-accent" />
            Rukma Nagar, AIIMS Road, Kheme Ka Kuwa, Jodhpur, Rajasthan 342008
          </div>
        </div>
      </div>
    </section>
  );
};
